import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { Avatar, Drawer, PinFilledIcon } from "components";
import dayjs from "dayjs";
import { memo } from "react";
import type { PinnedMessageItem } from "shared/types/chat";

interface PinnedChatsDrawerProps {
  open: boolean;
  onClose: () => void;
  pinnedMessages: PinnedMessageItem[];
  onUnpin: (chatId: string) => void;
  onPinnedMessageClick: (chatId: string) => void;
}

export const PinnedChatsDrawer = memo(function PinnedChatsDrawer({
  open,
  onClose,
  pinnedMessages,
  onUnpin,
  onPinnedMessageClick,
}: PinnedChatsDrawerProps) {
  const theme = useTheme();

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Pinned chats"
      drawerStyle={{ width: 400 }}
      footer={<></>}
    >
      <Box sx={{ padding: "8px 0" }}>
        {pinnedMessages.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%", p: "24px" }}
          >
            <Typography
              variant="body-medium"
              sx={{ color: theme.palette.lightBg.low }}
            >
              No pinned messages
            </Typography>
          </Stack>
        ) : (
          pinnedMessages?.map((pin) => {
            const { chat } = pin;

            return (
              <Stack
                key={pin.chat_id}
                direction="row"
                alignItems="flex-start"
                gap="12px"
                onClick={() => onPinnedMessageClick(pin.chat_id)}
                sx={{
                  padding: "16px",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: theme.palette.neutral["005"],
                  },
                }}
              >
                <Avatar
                  fullname={chat.sender?.display_name ?? ""}
                  url={chat.sender?.profile_pic}
                />
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" noWrap>
                    {chat.sender?.display_name}
                  </Typography>
                  <Typography
                    variant="body-medium"
                    noWrap
                    sx={{ color: theme.palette.lightBg.medium }}
                  >
                    {pin.excerpt ||
                      (pin.chat?.attachments?.length > 0
                        ? pin.chat.attachments.length > 1
                          ? `${pin.chat.attachments[0].file_name} +${pin.chat.attachments.length - 1} more`
                          : pin.chat.attachments[0].file_name
                        : "")}
                  </Typography>
                  <Typography
                    variant="body-small"
                    sx={{ color: theme.palette.lightBg.low }}
                  >
                    {dayjs(chat.created_at).isToday()
                      ? `Today ${dayjs(chat.created_at).format("h:mm A")}`
                      : dayjs(chat.created_at).format("MMM D, YYYY h:mm A")}
                  </Typography>
                </Stack>
                <IconButton
                  aria-label="Unpin"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnpin(pin.chat_id);
                  }}
                  sx={{ flexShrink: 0 }}
                >
                  <PinFilledIcon htmlColor={theme.palette.primary.main} />
                </IconButton>
              </Stack>
            );
          })
        )}
      </Box>
    </Drawer>
  );
});
